import React, { FC, useEffect, useState } from "react";
import { ButtonPrimary, ButtonSecondary, CheckboxInput } from "@shared/components";
import { Modal } from "@shared/components/Modal";
import { useUploadFileToReiSiftMutation } from "@shared/api/reiSiftApi";
import { useLazyGetExportableFilesToUploadQuery } from "@shared/api/exportablFilesApi";
import { useDispatch, useSelector } from "react-redux";
import { selectors as accountSelectors } from "@containers/Account/store";
import { ExportableFileType } from "@shared/interfaces";
import "./ModalSendFilesToReisift.scss";
import { startLoading, stopLoading } from "@shared/store/actions";

export type ModalReisiftIntegrationProps = {
  onClose: () => void;
  onSuccess: () => void;
  data?: {
    type: ExportableFileType;
  };
};

export const ModalSendFilesToReisift: FC<ModalReisiftIntegrationProps> = (props) => {
  const { onClose, data } = props;
  const dispatch = useDispatch();
  const account = useSelector(accountSelectors.getSelectedAccount());

  const [selected, setSelected] = useState<string[]>([]);

  const [searchFiles, { data: files, isFetching }] = useLazyGetExportableFilesToUploadQuery();
  const [uploadFileToReiSift] = useUploadFileToReiSiftMutation();

  useEffect(() => {
    if (account?.id && data?.type) {
      searchFiles({ account_id: account?.id, type: data.type });
    }
  }, [account?.id, data?.type, searchFiles]);

  useEffect(() => {
    if (isFetching) {
      dispatch(startLoading());
    } else {
      dispatch(stopLoading());
    }
  }, [isFetching, dispatch]);

  const selectFile = (id: string) => {
    setSelected((curr) => {
      if (curr.includes(id)) {
        return curr.filter((c) => c !== id);
      }

      return [...curr, id];
    });
  };

  const send = async () => {
    onClose();

    return Promise.allSettled(
      selected.map((fileId) =>
        uploadFileToReiSift({
          file_id: fileId,
          account_id: account?.id as number,
        }),
      ),
    );
  };

  return (
    <Modal>
      <Modal.CloseButton onClick={onClose} />
      <Modal.Section>
        <Modal.Header>
          <Modal.Title>Choose files to send to REISift</Modal.Title>
          <Modal.Text>Select files you would like transfer to REISift account.</Modal.Text>
        </Modal.Header>

        <Modal.Content>
          {files && !files?.total && <Modal.Text>There are no files to send.</Modal.Text>}
          {!!files?.total && (
            <div className="files-to-send">
              {files.results.map((file) => (
                <CheckboxInput
                  key={file.file_id}
                  value={+selected.includes(file.file_id)}
                  onChange={() => selectFile(file.file_id)}
                  label={file.name}
                />
              ))}
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
          <ButtonPrimary disabled={!selected.length} onClick={send}>
            Send
          </ButtonPrimary>
        </Modal.Actions>
      </Modal.Section>
    </Modal>
  );
};
